import React, { useRef } from 'react';
import { Theme, useTheme, CircularProgress, Box } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import { CcfCard, CcfTypography } from '@nice-ccf/ui-controls';
import { formatTimer } from '../../../util/common';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CcfContactPanelMediaControlProps {
  outBoundInitiatedStatus: boolean;
}

/**
 * Function gets called on click of mark as resloved button
 * @param event - React.MouseEvent
 * @example  markAsRelovedClicked (event)
 */
export function CcfContactPanelMediaControl(props: any) {
  const theme = useTheme();
  const audioEl: any = useRef();
  const [progress, setProgress] = React.useState(0);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        display: 'flex',
        width: '100%',
        boxShadow: 'none',
        flexDirection: 'column',
        alignItems: 'center',
      },

      controls: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-evenly',
      },

      playSeekIcon: {
        '&.MuiSvgIcon-root': {
          fill: props.outBoundInitiatedStatus ? '#C3C5C9' : '#007AB8',
          cursor: props.outBoundInitiatedStatus ? 'not-allowed' : 'pointer',
          fontSize: '2rem',
        },
      },
      playIcon: {
        '&.MuiSvgIcon-root': {
          fill: props.outBoundInitiatedStatus ? '#C3C5C9' : '#007AB8',
          cursor: props.outBoundInitiatedStatus ? 'not-allowed' : 'pointer',
          fontSize: '3.5rem',
        },
      },
      circle: {
        fill: '#D2D8DB',
      },
    })
  );

  const classes = useStyles();

  React.useEffect(() => {
    if (props.outBoundInitiatedStatus) {
      audioEl?.current?.pause();
    }
  }, [props.outBoundInitiatedStatus]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress(
        Math.round(
          (audioEl?.current?.currentTime.toFixed(0) * 100) /
            audioEl?.current?.duration
        )
      );
    }, 0);
    return () => {
      clearInterval(timer);
    };
  }, [audioEl?.current?.currentTime]);

  /**
   * Function gets called on click of mark as resloved button
   * @param event - React.MouseEvent
   * @example  markAsRelovedClicked (event)
   */
  const seekVoicemailAudio = (action: string) => {
    if (action === 'forward') {
      audioEl.current.currentTime = audioEl?.current.currentTime + 10;
    } else {
      audioEl.current.currentTime = audioEl?.current.currentTime - 10;
    }
  };

  /**
   * Function gets called on click of mark as resloved button
   * @param event -playAudio
   * @example  playAudio (event)
   */
  const playAudio = () => {
    if (audioEl?.current.paused) {
      audioEl?.current?.play();
    } else {
      audioEl?.current?.pause();
    }
  };

  return (
    <CcfCard className={classes.root} id="agent_voicemail_parentContainer">
      <div className={classes.controls} id="agent_voicemail_audio_container">
        <div
          aria-label="previous"
          id="agent_voicemail_previousAudio"
          onClick={() => seekVoicemailAudio('backward')}
        >
          {theme.direction === 'rtl' ? (
            <Forward10Icon className={classes.playSeekIcon} />
          ) : (
            <Replay10Icon className={classes.playSeekIcon} />
          )}
        </div>
        <div id="agent_voicemail_audioCircle" style={{ position: 'relative', maxHeight: '72px' }}>
          <CircularProgress
            classes={{
              circle: classes.circle,
            }}
            variant="determinate"
            value={progress}
            style={{ color: '#ADADAD', height: '60px', width: '60px' }}
          />
          {audioEl?.current?.paused ? (
            <Box
              position="relative"
              top="-63px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <PlayCircleFilledWhiteIcon
                className={classes.playIcon}
                onClick={playAudio}
              />
            </Box>
          ) : (
            <Box
              position="relative"
              top="-63px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <PauseCircleFilledIcon
                className={classes.playIcon}
                onClick={playAudio}
              />
            </Box>
          )}
        </div>
        <div id="agent_voicemail_forwardAudio" aria-label="next" onClick={() => seekVoicemailAudio('forward')}>
          {theme.direction === 'rtl' ? (
            <Replay10Icon className={classes.playSeekIcon} />
          ) : (
            <Forward10Icon className={classes.playSeekIcon} />
          )}
        </div>
      </div>
      <CcfTypography
        style={{
          fontFamily: 'Open Sans',
          fontSize: '14px',
          fontWeight: 'bolder',
          lineHeight: '19px',
          letterSpacing: '0.05em',
          textAlign: 'left',
          color: props.outBoundInitiatedStatus ? '#C3C5C9' : '#727272',
        }}
        id="agent_voicemail_timer"
      >
        {formatTimer(audioEl?.current?.currentTime.toFixed(0))} /{' '}
        {formatTimer(audioEl?.current?.duration.toFixed(0))}
      </CcfTypography>
      <audio ref={audioEl} src="/assets/audio/ziptonebip1.wav" hidden></audio>
    </CcfCard>
  );
}

export default CcfContactPanelMediaControl;
